/** FuryBot — Live Deals Flow */
import axios from "axios";
import { BOT_API } from "../constants";
import { track } from "../utils/analytics";
import { createDealCard } from "../utils/dataService";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { getAffiliateLink } from "@/utils/tracking";
import { parseSizeString, numToSizeLabel } from "@/utils/sizeUtils";

export function createDealsFlowHandlers(ctx) {
  const { firmsRef, promosRef, firms, promos, addBot, setMessages, setFirms, setPromos, ensureFirms } = ctx;

  const startDealsFlow = () => {
    track("flow_started", { flow: "deals" });
    addBot("Want to see deals for a specific firm, or all active deals?", [
      { label: "Specific firm", action: "deals_firm" },
      { label: "Show all active deals", action: "deals_all" },
    ]);
  };

  const showAllDeals = async () => {
    const f = await ensureFirms();
    const p = promosRef.current;

    const withFd = f.map(ff => ({ ff, fd: formatFirmDisplay(ff) }));
    const standardDeals = withFd
      .filter(({ fd }) => fd.discountValid)
      .sort((a, b) => (b.fd.discountPercent || 0) - (a.fd.discountPercent || 0))
      .map(({ ff }) => ff);

    const flashDeals = p.filter(pp => pp.is_active && pp.show_on_discount_page);

    if (p.length === 0) {
      axios.get(`${BOT_API}/promos/active`).then(r => {
        promosRef.current = r.data || [];
        setPromos(r.data || []);
      }).catch(() => {});
    }

    const totalDeals = standardDeals.length + flashDeals.length;
    if (totalDeals === 0) {
      addBot("No active deals right now. Check back soon — firms run flash sales frequently!", [
        { label: "Help me pick a firm", action: "flow_match" },
        { label: "See all discounts page", action: "link", data: "/futures-prop-firm-discounts" },
        { label: "Main menu", action: "home" },
      ]);
      return;
    }

    const cards = standardDeals.map(ff => createDealCard(ff, p));
    const standardSlugs = new Set(standardDeals.map(ff => ff.slug));
    const flashOnlyFirms = new Set();
    flashDeals.forEach(fd => {
      const firmSlug = f.find(ff => ff.name === fd.firm_name)?.slug;
      if (firmSlug && !standardSlugs.has(firmSlug)) flashOnlyFirms.add(firmSlug);
    });
    flashOnlyFirms.forEach(slug => {
      const firm = f.find(ff => ff.slug === slug);
      if (firm) cards.push(createDealCard(firm, p));
    });

    track("deal_viewed", { count: cards.length, flash_count: flashDeals.length });

    let text = `${cards.length} firms with active deals`;
    if (flashDeals.length > 0) text += ` (including ${flashDeals.length} flash sale${flashDeals.length > 1 ? "s" : ""})`;
    text += ":";

    setMessages(m => [...m, { role: "bot", text, cards, buttons: [
      { label: "Which is best for me?", action: "flow_match" },
      { label: "Compare firms", action: "flow_compare" },
      { label: "Full discounts page", action: "link", data: "/futures-prop-firm-discounts" },
      { label: "Main menu", action: "home" },
    ], ts: Date.now() }]);
  };

  const showFirmPicker = async (prefix) => {
    const f = await ensureFirms();
    if (f.length === 0) {
      addBot("I'm fetching the latest firm data. If it takes a moment, you can browse directly:", [
        { label: "All firms", action: "link", data: "/futures-prop-firm-comparison" },
        { label: "Try again", action: prefix === "deals" ? "deals_firm" : "flow_compare" },
      ]);
      return;
    }
    const buttons = f.map(ff => ({ label: `${ff.name}`, action: `${prefix === "deals" ? "deal_firm_" : prefix === "compare1" ? "compare1_" : "compare2_"}${ff.slug}`, data: ff.slug }));
    addBot("Which firm?", buttons);
  };

  const showFirmDeal = (slug) => {
    const f = (firmsRef.current.length > 0 ? firmsRef.current : firms).find(ff => ff.slug === slug);
    if (!f) {
      addBot("I'm fetching the latest details for that firm right now. If it takes a moment, you can open the full firm page below.", [
        { label: "View firm page", action: "link", data: `/firms/${slug}` },
        { label: "View discount page", action: "link", data: `/${slug}-discount` },
        { label: "Try again", action: `deal_firm_${slug}` },
        { label: "Main menu", action: "home" },
      ]);
      axios.get(`${BOT_API}/firms`).then(r => { firmsRef.current = r.data || []; setFirms(r.data || []); }).catch(() => {});
      return;
    }
    const card = createDealCard(f, promos);
    track("deal_viewed", { firm: f.name });
    setMessages(m => [...m, { role: "bot", text: `Here's what's available for ${f.name}:`, cards: [card], buttons: [
      { label: "Is this good for me?", action: "flow_match" },
      { label: "Compare this firm", action: "flow_compare" },
      { label: "All deals", action: "deals_all" },
      { label: "Main menu", action: "home" },
    ], ts: Date.now() }]);
  };

  const showBothFirmDeals = (slug1, slug2, size) => {
    const f = firmsRef.current.length > 0 ? firmsRef.current : firms;
    const f1 = f.find(ff => ff.slug === slug1);
    const f2 = f.find(ff => ff.slug === slug2);
    if (!f1 || !f2) return;
    const numericSize = typeof size === 'number' ? size : (size ? parseSizeString(size) : 50000);
    const sizeLabel = numToSizeLabel(numericSize);
    track("deals_both_compared", { firm1: f1.name, firm2: f2.name, size: sizeLabel });
    const card1 = createDealCard(f1, promos, numericSize);
    const card2 = createDealCard(f2, promos, numericSize);
    setMessages(m => [...m, { role: "bot", text: `Discounts for ${f1.name} & ${f2.name} (${sizeLabel}):`, cards: [card1, card2], buttons: [
      { label: `${f1.name} profile`, action: "link", data: `/firms/${f1.slug}` },
      { label: `${f2.name} profile`, action: "link", data: `/firms/${f2.slug}` },
      { label: "Compare rules", action: "flow_rules" },
      { label: "Which is better for me?", action: "flow_match" },
    ], ts: Date.now() }]);
  };

  return { startDealsFlow, showAllDeals, showFirmPicker, showFirmDeal, showBothFirmDeals };
}
